import { useState } from 'react';
import { DEV_SERVER } from '../constants';
import { usePolling } from '../hooks/usePolling';
import { card, labelSecondary } from '../styles';
import type {
  CloudflareMetrics,
  CostSummary,
  ExpoMetrics,
  FlyAppMetrics,
  GitHubMetrics,
  HealthCheck,
  MonitoringData,
  ResendMetrics,
  SentryMetrics,
  SentryProjectStats,
  ServiceTier,
  SupabaseProjectMetrics,
  TolgeeMetrics,
  UsagePrediction,
} from '../types';

// ============================================================================
// HELPER: Safe number formatting
// ============================================================================

function safeNum(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function fmt(v: unknown): string {
  return safeNum(v).toLocaleString();
}

function fmtPct(v: unknown, decimals = 1): string {
  return `${safeNum(v).toFixed(decimals)}%`;
}

// ============================================================================
// SHARED STYLES
// ============================================================================

const CAPTION: React.CSSProperties = {
  fontSize: 'var(--font-size-caption)',
  lineHeight: 'var(--line-height-caption)',
};

const LABEL_TERT: React.CSSProperties = {
  ...CAPTION,
  color: 'var(--color-label-tertiary)',
};

// Note: color-label-quaternary = disabled/decorative only. Readable suppressed text = tertiary.

const ERROR_TEXT: React.CSSProperties = {
  ...CAPTION,
  color: 'var(--color-tint-red)',
};

const SECTION_DIVIDER: React.CSSProperties = {
  borderTop: '1px solid var(--color-border-transparent)',
  paddingTop: 8,
  marginTop: 2,
};

// ============================================================================
// COST METRIC — primary display for pay-as-you-go cards
// ============================================================================

function CostMetric({
  mtd,
  estimate,
  isTracked,
  state,
  detail,
}: {
  mtd?: number | null;
  estimate: number;
  isTracked?: boolean;
  state?: string;
  detail?: string;
}) {
  const hasMtd = isTracked && mtd != null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        {/* MTD */}
        <div>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1,
              color: hasMtd ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
            }}
          >
            ${hasMtd ? mtd?.toFixed(2) : '—'}
          </span>
          <span style={{ ...LABEL_TERT, fontSize: 10, marginLeft: 3 }}>MTD</span>
        </div>
        {/* Estimated */}
        <div>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1,
              color: 'var(--color-label-primary)',
            }}
          >
            ~${estimate.toFixed(2)}
          </span>
          <span style={{ ...LABEL_TERT, fontSize: 10, marginLeft: 3 }}>est./mo</span>
        </div>
        {state && (
          <span
            style={{
              fontSize: 11,
              color:
                state === 'started' ? 'var(--color-tint-green)' : 'var(--color-label-tertiary)',
            }}
          >
            · {state}
          </span>
        )}
      </div>
      {detail && <div style={{ ...LABEL_TERT, marginTop: 1 }}>{detail}</div>}
    </div>
  );
}

// ============================================================================
// HEALTH STATUS RESOLUTION
// ============================================================================

type HealthStatus = 'up' | 'degraded' | 'down' | 'not_configured' | 'unknown';

function resolveHealthStatus(raw: string): HealthStatus {
  if (
    raw === 'online' ||
    raw === 'started' ||
    raw === 'ok' ||
    raw === 'success' ||
    raw === 'active' ||
    raw === 'verified' ||
    raw === 'ACTIVE_HEALTHY' ||
    raw === 'up'
  )
    return 'up';
  if (
    raw === 'degraded' ||
    raw === 'warning' ||
    raw === 'suspended' ||
    raw === 'in_progress' ||
    raw === 'queued' ||
    raw === 'approaching'
  )
    return 'degraded';
  if (raw === 'not_configured' || raw === 'unknown') return 'not_configured';
  return 'down';
}

// ============================================================================
// STATUS DOT (health: green=up, orange=degraded, red=down, gray=not_configured)
// ============================================================================

function StatusDot({ health }: { health: HealthStatus }) {
  const color =
    health === 'up'
      ? 'var(--color-tint-green)'
      : health === 'degraded'
        ? 'var(--color-tint-orange)'
        : health === 'down'
          ? 'var(--color-tint-red)'
          : 'var(--color-label-quaternary)';
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

// ============================================================================
// TIER BADGE
// ============================================================================

type TierType =
  | 'Free'
  | 'Developer'
  | 'Pay-as-you-go'
  | 'Pro'
  | 'Not configured'
  | 'Annual'
  | string;

function TierBadge({ tier }: { tier: TierType }) {
  let bg: string;
  let fg: string;
  let border: string | undefined;

  switch (tier) {
    case 'Free':
      bg = 'var(--color-fill-tertiary)';
      fg = 'var(--color-label-secondary)';
      break;
    case 'Developer':
      bg = 'var(--color-tint-blue)';
      fg = '#fff';
      break;
    case 'Pay-as-you-go':
      bg = 'var(--color-tint-purple)';
      fg = '#fff';
      break;
    case 'Pro':
      bg = 'var(--color-tint-green)';
      fg = '#fff';
      break;
    case 'Annual':
      bg = 'var(--color-tint-indigo)';
      fg = '#fff';
      break;
    case 'Not configured':
      bg = 'transparent';
      fg = 'var(--color-label-quaternary)';
      border = '1px dashed var(--color-label-quaternary)';
      break;
    default:
      bg = 'var(--color-fill-tertiary)';
      fg = 'var(--color-label-secondary)';
  }

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-caption)',
        fontWeight: 600,
        background: bg,
        color: fg,
        border: border ?? 'none',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {tier}
    </span>
  );
}

// ============================================================================
// QUOTA BAR (color based ONLY on quota %, never on health)
// ============================================================================

function QuotaBar({ used, limit, unit }: { used: number; limit: number; unit?: string }) {
  const safeUsed = safeNum(used);
  const safeLimit = safeNum(limit);
  const pct = safeLimit > 0 ? Math.min((safeUsed / safeLimit) * 100, 100) : 0;
  const barColor =
    pct >= 90
      ? 'var(--color-tint-red)'
      : pct >= 60
        ? 'var(--color-tint-orange)'
        : 'var(--color-tint-green)';
  const u = unit ?? '';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'var(--color-label-tertiary)',
          marginBottom: 3,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase' as const,
            color: 'var(--color-label-tertiary)',
          }}
        >
          QUOTA
        </span>
        <span>
          {fmt(safeUsed)}
          {u} / {fmt(safeLimit)}
          {u} ({fmtPct(pct)})
        </span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--color-fill-tertiary)' }}>
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 2,
            background: barColor,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// SPARKLINE (tiny bar chart for Sentry daily data)
// ============================================================================

function Sparkline({ data, height = 20 }: { data: number[]; height?: number }) {
  if (!data?.length) return null;
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height }}>
      {data.map((v, i) => {
        const h = Math.max((v / max) * height, 1);
        const barColor =
          v === 0
            ? 'var(--color-fill-tertiary)'
            : v > 10
              ? 'var(--color-tint-red)'
              : 'var(--color-tint-orange)';
        return (
          <div
            key={`bar-${i}-${v}`}
            style={{ width: 3, height: h, borderRadius: 1, background: barColor }}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// PREDICTION LINE
// ============================================================================

function PredictionLine({ prediction }: { prediction?: UsagePrediction }) {
  if (!prediction) return null;
  return (
    <div style={{ ...LABEL_TERT, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {safeNum(prediction.projected_this_month) > 0 && (
        <span>~{fmt(prediction.projected_this_month)} projected this month</span>
      )}
      {prediction.days_to_limit != null && prediction.days_to_limit < 365 && (
        <span
          style={{
            color:
              prediction.status === 'critical' || prediction.status === 'exceeded'
                ? 'var(--color-tint-red)'
                : 'var(--color-tint-orange)',
          }}
        >
          ~{Math.round(prediction.days_to_limit).toLocaleString()} days to limit
        </span>
      )}
    </div>
  );
}

// ============================================================================
// UPGRADE HINT
// ============================================================================

function UpgradeHint({ tier }: { tier?: ServiceTier }) {
  if (!tier?.next) return null;
  return (
    <div style={LABEL_TERT}>
      Upgrade:{' '}
      <span style={{ color: 'var(--color-label-tertiary)', fontWeight: 500 }}>
        {tier.next.tier} (${tier.next.price}/mo)
      </span>
      {tier.next.url && (
        <>
          {' '}
          <a
            href={tier.next.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-tint-blue)', textDecoration: 'none' }}
          >
            Pricing
          </a>
        </>
      )}
    </div>
  );
}

// ============================================================================
// INLINE STATUS BADGE (e.g. "started", "ACTIVE_HEALTHY")
// ============================================================================

function InlineBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 'var(--radius-full)',
        ...CAPTION,
        fontWeight: 600,
        background: color,
        color: '#fff',
      }}
    >
      {label}
    </span>
  );
}

// ============================================================================
// SERVICE CARD SHELL (unified card with expand/collapse)
// ============================================================================

interface ServiceCardProps {
  title: string;
  health: HealthStatus;
  tier: TierType;
  isNotConfigured?: boolean;
  warningText?: string;
  costLine?: string;
  quotaBar?: React.ReactNode;
  keyStatus?: React.ReactNode;
  details?: React.ReactNode;
  hasDetails?: boolean;
}

function ServiceCard({
  title,
  health,
  tier,
  isNotConfigured,
  warningText,
  costLine,
  quotaBar,
  keyStatus,
  details,
  hasDetails = true,
}: ServiceCardProps) {
  const [expanded, setExpanded] = useState(false);

  const borderColor = isNotConfigured
    ? undefined
    : health === 'down'
      ? 'var(--color-tint-red)'
      : health === 'degraded'
        ? 'var(--color-tint-orange)'
        : 'var(--color-border-opaque)';

  return (
    <div
      style={{
        ...card,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 160,
        border: isNotConfigured
          ? '1px dashed var(--color-label-quaternary)'
          : `1px solid ${borderColor}`,
        opacity: isNotConfigured ? 0.7 : 1,
      }}
    >
      {/* Header: dot + name + tier badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <StatusDot health={isNotConfigured ? 'not_configured' : health} />
        <span
          style={{
            flex: 1,
            fontWeight: 600,
            fontSize: 'var(--font-size-body)',
            color: isNotConfigured ? 'var(--color-label-tertiary)' : 'var(--color-label-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' as const,
          }}
        >
          {title}
        </span>
        <TierBadge tier={tier} />
      </div>

      {/* Warning strip */}
      {warningText && (
        <div
          style={{
            padding: '4px 8px',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(255,59,48,0.08)',
            fontSize: 11,
            color: 'var(--color-tint-red)',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          {warningText}
        </div>
      )}

      {/* Cost line */}
      {costLine && (
        <div
          style={{
            fontSize: 'var(--font-size-caption)',
            color: 'var(--color-label-tertiary)',
            marginBottom: 8,
            fontWeight: 500,
          }}
        >
          {costLine}
        </div>
      )}

      {/* Quota bar OR Key status */}
      <div style={{ marginBottom: 8 }}>{quotaBar ?? keyStatus}</div>

      {/* Expanded details */}
      {expanded && details && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginBottom: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--color-border-transparent)',
          }}
        >
          {details}
        </div>
      )}

      {/* Spacer pushes footer to bottom */}
      <div style={{ flex: 1 }} />

      {/* Footer: Show more/less */}
      {hasDetails && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px 0 0',
            cursor: 'pointer',
            fontSize: 11,
            color: 'var(--color-label-tertiary)',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            borderTop: '1px solid var(--color-border-transparent)',
            marginTop: 4,
          }}
        >
          {expanded ? 'Show less \u25B4' : 'Show more \u25BE'}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// CARD DEFINITION (for sorting)
// ============================================================================

interface CardDef {
  key: string;
  name: string;
  tier: TierType;
  health: HealthStatus;
  quotaStatus: 'ok' | 'approaching' | 'critical' | 'exceeded';
  sortPriority: number;
  hasWarning: boolean;
  isNotConfigured: boolean;
  render: () => React.ReactNode;
}

// ============================================================================
// CARD BUILDERS
// ============================================================================

// ---- Fly.io (Pay-as-you-go) ------------------------------------------------

function buildFlyCard(
  label: string,
  app: FlyAppMetrics | undefined,
  tier: ServiceTier | undefined,
  health: HealthCheck | undefined,
  costEst?: number,
  billing?: import('../types').FlyBilling,
): CardDef {
  const key = label.toLowerCase().replace(/\s+/g, '-');
  const machine = app?.machines?.[0];
  const machineState = machine?.state ?? 'unknown';
  const healthStatus = resolveHealthStatus(machineState);
  const isProd = label.includes('Production');
  // costEst: from backend — tracked uptime cost (>1h) or static estimate (<1h)
  const costNum = costEst != null && costEst > 0 ? costEst : safeNum(tier?.price_est_mo);
  // Fly GraphQL billing = org-wide MTD (both apps combined)
  const realMtd = billing?.monthly_usd != null && !billing?.error ? billing.monthly_usd : null;
  const machineDetail = machine
    ? `${machine.region} · ${machine.cpus} vCPU · ${machine.memory_mb} MB RAM`
    : undefined;

  return {
    key,
    name: label,
    tier: 'Pay-as-you-go',
    health: healthStatus,
    quotaStatus: 'ok',
    sortPriority: 4,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title={label}
        health={healthStatus}
        tier="Pay-as-you-go"
        quotaBar={
          app?.error ? (
            <div style={ERROR_TEXT}>{app.error}</div>
          ) : (
            <CostMetric
              mtd={realMtd}
              estimate={costNum}
              isTracked={realMtd != null}
              state={machineState}
              detail={machineDetail}
            />
          )
        }
        hasDetails={!!machine}
        details={
          machine ? (
            <>
              {health && (
                <div style={LABEL_TERT}>
                  Health: {health.status} ({safeNum(health.latencyMs)}ms)
                </div>
              )}
              <div style={LABEL_TERT}>Machine: {machine.id}</div>
              {machine.created_at && (
                <div style={LABEL_TERT}>
                  Created: {new Date(machine.created_at).toLocaleDateString()}
                </div>
              )}
              {machine.updated_at && (
                <div style={LABEL_TERT}>
                  Updated: {new Date(machine.updated_at).toLocaleDateString()}
                </div>
              )}
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Supabase (Pro org + per-project compute) --------------------------------

function buildSupabaseCard(
  label: string,
  metrics: SupabaseProjectMetrics | undefined,
  tier: ServiceTier | undefined,
  isFirstProject?: boolean,
): CardDef {
  const key = label.toLowerCase().replace(/\s+/g, '-');
  const status = metrics?.status ?? 'unknown';
  const isHealthy = status === 'ACTIVE_HEALTHY';
  const healthStatus: HealthStatus = isHealthy ? 'up' : resolveHealthStatus(status);

  // Supabase: Pro org base ($25/mo) for first project; compute credits cover Micro compute
  const computeName = metrics?.compute_name ?? 'Micro';
  const computeVcpus = metrics?.compute_vcpus ?? 2;
  const computeRamGb = metrics?.compute_ram_gb ?? 1;
  const costLabel = isFirstProject
    ? `Pro plan: $25/mo · ${computeName} (${computeVcpus} vCPU, ${computeRamGb} GB)`
    : `${computeName} compute · $10/mo offset by credits`;

  // DB size quota (Pro limit: 8 GB)
  const dbSizeBytes = metrics?.db_size_bytes ?? null;
  const dbLimitBytes = 8 * 1024 * 1024 * 1024; // 8 GB
  const dbSizeMb = dbSizeBytes != null ? Math.round(dbSizeBytes / 1024 / 1024) : null;
  const dbSizeGb = dbSizeBytes != null ? dbSizeBytes / 1024 / 1024 / 1024 : null;

  return {
    key,
    name: label,
    tier: 'Pro',
    health: healthStatus,
    quotaStatus: 'ok',
    sortPriority: 6,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title={label}
        health={healthStatus}
        tier="Pro"
        costLine={costLabel}
        quotaBar={
          metrics?.error ? (
            <div style={ERROR_TEXT}>{metrics.error}</div>
          ) : dbSizeMb != null ? (
            <QuotaBar used={dbSizeMb} limit={8 * 1024} unit=" MB" />
          ) : (
            <div style={LABEL_TERT}>DB size loading…</div>
          )
        }
        keyStatus={
          metrics && !metrics.error ? (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}
            >
              <InlineBadge
                label={status}
                color={isHealthy ? 'var(--color-tint-green)' : 'var(--color-tint-orange)'}
              />
              <span style={LABEL_TERT}>
                {computeName} &middot; {computeVcpus} vCPU &middot; {computeRamGb} GB
              </span>
            </div>
          ) : undefined
        }
        details={
          metrics && !metrics.error ? (
            <>
              {(metrics.region || metrics.db_version) && (
                <div style={LABEL_TERT}>
                  {metrics.region && <span>{metrics.region}</span>}
                  {metrics.region && metrics.db_version && <span> &middot; </span>}
                  {metrics.db_version && <span>PostgreSQL {metrics.db_version}</span>}
                </div>
              )}
              {dbSizeGb != null && (
                <div style={LABEL_TERT}>
                  DB: {dbSizeGb < 1 ? `${dbSizeMb} MB` : `${dbSizeGb.toFixed(2)} GB`} of 8 GB Pro
                  limit
                </div>
              )}
              <div style={LABEL_TERT}>
                Pro limits: 8 GB DB &middot; 250 GB BW &middot; 100 GB Storage &middot; 100k MAU
              </div>
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
        hasDetails={!!metrics && !metrics.error}
      />
    ),
  };
}

// ---- Sentry Error Tracking (Developer Free) ---------------------------------

function buildSentryCard(
  sentry: SentryMetrics | undefined,
  tier: ServiceTier | undefined,
): CardDef {
  const projects: {
    key: 'zoobook-web' | 'zoobook-postgraphile' | 'zoobook-mobile';
    label: string;
  }[] = [
    { key: 'zoobook-web', label: 'Web' },
    { key: 'zoobook-postgraphile', label: 'PostGraphile' },
    { key: 'zoobook-mobile', label: 'Mobile' },
  ];

  const totalMonth = projects.reduce((sum, p) => sum + safeNum(sentry?.[p.key]?.total_month), 0);
  const limit = safeNum(tier?.limits?.errors_mo) || 5000;
  const prediction = sentry?._prediction;
  const pctUsed = limit > 0 ? (totalMonth / limit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctUsed >= 90 ? 'critical' : pctUsed >= 60 ? 'approaching' : 'ok';

  return {
    key: 'sentry',
    name: 'Sentry',
    tier: 'Developer',
    health: sentry?.error ? 'down' : 'up',
    quotaStatus,
    sortPriority: 5,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Sentry Error Tracking"
        health={sentry?.error ? 'down' : 'up'}
        tier="Developer"
        costLine="Free (Developer plan) -- 5,000 errors/month"
        quotaBar={
          sentry?.error ? (
            <div style={ERROR_TEXT}>{sentry.error}</div>
          ) : (
            <QuotaBar used={totalMonth} limit={limit} />
          )
        }
        details={
          !sentry?.error ? (
            <>
              {/* Per-project breakdown */}
              {projects.map(({ key, label }) => {
                const stats: SentryProjectStats | undefined = sentry?.[key];
                if (!stats) return null;
                if (stats.error)
                  return (
                    <div key={key} style={ERROR_TEXT}>
                      {label}: {stats.error}
                    </div>
                  );
                return (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span
                        style={{
                          ...CAPTION,
                          fontWeight: 600,
                          color: 'var(--color-label-secondary)',
                        }}
                      >
                        {label}
                      </span>
                      <span
                        style={{
                          ...CAPTION,
                          fontWeight: 700,
                          color:
                            safeNum(stats.total_month) === 0
                              ? 'var(--color-tint-green)'
                              : safeNum(stats.total_month) > 50
                                ? 'var(--color-tint-red)'
                                : 'var(--color-tint-orange)',
                        }}
                      >
                        {fmt(stats.total_month)}
                      </span>
                    </div>
                    <Sparkline data={stats.daily ?? []} height={18} />
                  </div>
                );
              })}
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- GitHub CI/CD (Free) ----------------------------------------------------

function buildGitHubCard(
  github: GitHubMetrics | undefined,
  tier: ServiceTier | undefined,
): CardDef {
  const limit = safeNum(tier?.limits?.ci_min) || 2000;
  const used = safeNum(github?.ci_minutes_est);
  const prediction = github?._prediction;
  // Health in monitoring = API reachable (not CI workflow results — that's a Deploy concern)
  const healthStatus: HealthStatus = github?.error ? 'down' : 'up';
  const pctUsed = limit > 0 ? (used / limit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctUsed >= 90 ? 'critical' : pctUsed >= 60 ? 'approaching' : 'ok';

  return {
    key: 'github',
    name: 'GitHub CI/CD',
    tier: 'Free',
    health: healthStatus,
    quotaStatus,
    sortPriority: 5,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="GitHub CI/CD"
        health={healthStatus}
        tier="Free"
        costLine="Free -- 2,000 min/month"
        quotaBar={
          github?.error ? (
            <div style={ERROR_TEXT}>{github.error}</div>
          ) : (
            <QuotaBar used={used} limit={limit} unit=" min" />
          )
        }
        details={
          github && !github.error ? (
            <>
              {/* Workflow run dots */}
              {(github.last_runs ?? []).length > 0 && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(github.last_runs ?? []).slice(0, 8).map((run) => {
                    const dotColor =
                      run.conclusion === 'success'
                        ? 'var(--color-tint-green)'
                        : run.conclusion === 'failure'
                          ? 'var(--color-tint-red)'
                          : run.status === 'in_progress'
                            ? 'var(--color-tint-orange)'
                            : 'var(--color-fill-tertiary)';
                    return (
                      <span
                        key={run.id}
                        title={`${run.name}: ${run.conclusion ?? run.status}`}
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: dotColor,
                          display: 'inline-block',
                        }}
                      />
                    );
                  })}
                </div>
              )}
              <div style={LABEL_TERT}>
                {fmt(github.runs_this_month)} runs &middot; {safeNum(github.success_rate)}% success
              </div>
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Cloudflare Pages (Free) ------------------------------------------------

function buildCloudflareCard(
  cloudflare: CloudflareMetrics | undefined,
  tier: ServiceTier | undefined,
): CardDef {
  const limit = safeNum(tier?.limits?.builds_mo) || 500;
  const used = safeNum(cloudflare?.builds_this_month);
  const prediction = cloudflare?._prediction;
  const pctUsed = limit > 0 ? (used / limit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctUsed >= 90 ? 'critical' : pctUsed >= 60 ? 'approaching' : 'ok';
  const latestStatus = cloudflare?.latest?.status;
  const latestFailed = latestStatus === 'failure';
  // Health = API reachable; deployment failures are shown as a status note in details, not as service health
  const healthStatus: HealthStatus = cloudflare?.error ? 'down' : 'up';

  return {
    key: 'cloudflare-pages',
    name: 'Cloudflare Pages',
    tier: 'Free',
    health: healthStatus,
    quotaStatus,
    sortPriority: 5,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Cloudflare Pages"
        health={healthStatus}
        tier="Free"
        costLine="Free -- 500 builds/month"
        quotaBar={
          cloudflare?.error ? (
            <div style={ERROR_TEXT}>{cloudflare.error}</div>
          ) : (
            <QuotaBar used={used} limit={limit} />
          )
        }
        details={
          cloudflare && !cloudflare.error ? (
            <>
              {cloudflare.latest && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...CAPTION }}>
                    <span style={{ color: 'var(--color-label-secondary)', fontWeight: 600 }}>
                      Latest
                    </span>
                    <span style={{ color: 'var(--color-label-tertiary)' }}>
                      {cloudflare.latest.environment}
                    </span>
                    <InlineBadge
                      label={cloudflare.latest.status ?? 'unknown'}
                      color={
                        latestFailed
                          ? 'var(--color-tint-red)'
                          : latestStatus === 'success'
                            ? 'var(--color-tint-green)'
                            : 'var(--color-tint-orange)'
                      }
                    />
                  </div>
                  {cloudflare.latest.url && (
                    <a
                      href={cloudflare.latest.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        ...CAPTION,
                        color: 'var(--color-tint-blue)',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      {cloudflare.latest.url}
                    </a>
                  )}
                </div>
              )}
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Resend Email (Free) ----------------------------------------------------

function buildResendCard(
  resend: ResendMetrics | undefined,
  tier: ServiceTier | undefined,
): CardDef {
  const limit = safeNum(tier?.limits?.monthly) || 3000;
  const used = safeNum(resend?.emails_this_month);
  const prediction = resend?._prediction;
  const domainOk = resend?.domain_status === 'verified';
  const restricted = resend?.api_restricted ?? false;
  const pctUsed = limit > 0 ? (used / limit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctUsed >= 90 ? 'critical' : pctUsed >= 60 ? 'approaching' : 'ok';
  const healthStatus: HealthStatus = restricted ? 'degraded' : domainOk ? 'up' : 'degraded';

  return {
    key: 'resend',
    name: 'Resend Email',
    tier: 'Free',
    health: healthStatus,
    quotaStatus,
    sortPriority: restricted ? 3 : 5,
    hasWarning: restricted,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Resend Email"
        health={healthStatus}
        tier="Free"
        warningText={restricted ? 'API restricted -- check Resend dashboard' : undefined}
        costLine="Free -- 3,000 emails/month"
        quotaBar={
          resend?.error ? (
            <div style={ERROR_TEXT}>{resend.error}</div>
          ) : (
            <QuotaBar used={used} limit={limit} />
          )
        }
        details={
          resend && !resend.error ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...CAPTION }}>
                <InlineBadge
                  label={domainOk ? 'verified' : (resend.domain_status ?? 'unknown')}
                  color={domainOk ? 'var(--color-tint-green)' : 'var(--color-tint-orange)'}
                />
                <span style={{ color: 'var(--color-label-secondary)', fontWeight: 600 }}>
                  {resend.domain ?? 'zoobook.online'}
                </span>
              </div>
              <div style={LABEL_TERT}>100/day limit</div>
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Expo EAS (Free) --------------------------------------------------------

function buildExpoCard(expo: ExpoMetrics | undefined, tier: ServiceTier | undefined): CardDef {
  const buildLimit = safeNum(tier?.limits?.builds_mo) || 30;
  const buildsUsed = safeNum(expo?.builds_this_month);
  const iosUsed = safeNum(expo?.ios_this_month);
  const androidUsed = Math.max(buildsUsed - iosUsed, 0);
  const prediction = expo?._prediction;
  const pctBuilds = buildLimit > 0 ? (buildsUsed / buildLimit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctBuilds >= 90 ? 'critical' : pctBuilds >= 60 ? 'approaching' : 'ok';

  return {
    key: 'expo',
    name: 'Expo EAS',
    tier: 'Free',
    health: expo?.error ? 'down' : 'up',
    quotaStatus,
    sortPriority: 5,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Expo EAS"
        health={expo?.error ? 'down' : 'up'}
        tier="Free"
        costLine="Free -- 30 builds/month"
        quotaBar={
          expo?.error ? (
            <div style={ERROR_TEXT}>{expo.error}</div>
          ) : (
            <QuotaBar used={buildsUsed} limit={buildLimit} />
          )
        }
        details={
          expo && !expo.error ? (
            <>
              <div style={LABEL_TERT}>
                {fmt(iosUsed)} iOS &middot; {fmt(androidUsed)} Android
              </div>
              {/* Recent builds list */}
              {(expo.recent ?? []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span
                    style={{ ...CAPTION, color: 'var(--color-label-tertiary)', fontWeight: 600 }}
                  >
                    Recent
                  </span>
                  {(expo.recent ?? []).slice(0, 4).map((b) => {
                    const sColor =
                      b.status === 'finished'
                        ? 'var(--color-tint-green)'
                        : b.status === 'errored'
                          ? 'var(--color-tint-red)'
                          : 'var(--color-tint-orange)';
                    return (
                      <div
                        key={b.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, ...CAPTION }}
                      >
                        <span
                          style={{
                            padding: '0 4px',
                            borderRadius: 'var(--radius-sm)',
                            background: sColor,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 10,
                            textTransform: 'uppercase' as const,
                          }}
                        >
                          {b.platform}
                        </span>
                        <span style={{ color: 'var(--color-label-secondary)' }}>{b.status}</span>
                        <span style={{ color: 'var(--color-label-tertiary)', marginLeft: 'auto' }}>
                          {new Date(b.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Tolgee i18n (Free) -----------------------------------------------------

function buildTolgeeCard(
  tolgee: TolgeeMetrics | undefined,
  tier: ServiceTier | undefined,
): CardDef {
  const limit = safeNum(tier?.limits?.keys) || 1000;
  const used = safeNum(tolgee?.key_count);
  const prediction = tolgee?._prediction;
  const pctUsed = limit > 0 ? (used / limit) * 100 : 0;
  const quotaStatus: CardDef['quotaStatus'] =
    pctUsed >= 90 ? 'critical' : pctUsed >= 60 ? 'approaching' : 'ok';
  const translatedPct = Number.isFinite(tolgee?.translated_pct) ? tolgee?.translated_pct : 0;

  return {
    key: 'tolgee',
    name: 'Tolgee i18n',
    tier: 'Free',
    health: tolgee?.error ? 'down' : 'up',
    quotaStatus,
    sortPriority: 5,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Tolgee i18n"
        health={tolgee?.error ? 'down' : 'up'}
        tier="Free"
        costLine="Free -- 1,000 keys"
        quotaBar={
          tolgee?.error ? (
            <div style={ERROR_TEXT}>{tolgee.error}</div>
          ) : (
            <QuotaBar used={used} limit={limit} unit=" keys" />
          )
        }
        details={
          tolgee && !tolgee.error ? (
            <>
              <div style={LABEL_TERT}>
                {safeNum(tolgee.language_count)} languages &middot; {safeNum(tolgee.members_count)}{' '}
                members &middot; {translatedPct}% translated
              </div>
              {(tolgee.languages ?? []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {tolgee.languages.map((lang) => (
                    <div
                      key={lang.tag}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...CAPTION,
                      }}
                    >
                      <span style={{ color: 'var(--color-label-secondary)' }}>
                        {lang.tag.toUpperCase()} {lang.name}
                      </span>
                      <span style={{ color: 'var(--color-label-tertiary)' }}>
                        {safeNum(lang.translated)}/{safeNum(lang.total)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <PredictionLine prediction={prediction} />
              <UpgradeHint tier={tier} />
            </>
          ) : undefined
        }
      />
    ),
  };
}

// ---- Anthropic (Pay-as-you-go) ----------------------------------------------

function buildAnthropicCard(cost: CostSummary | undefined): CardDef {
  // anthropic in breakdown = actual tracked MTD token cost (0 if no calls yet)
  // If backend fell back to static estimate ($1), days_elapsed would be set
  const trackedMtd = safeNum(cost?.breakdown?.anthropic);
  const daysElapsed = cost?.days_elapsed ?? new Date().getUTCDate();
  const daysInMonth = cost?.days_in_month ?? 30;
  const mtdRatio = Math.max(daysElapsed / daysInMonth, 0.01);
  // Project to full month only when we have real tracked data (not static fallback)
  // Static fallback = no usage_tracker calls yet = trackedMtd is the $1 static estimate
  // We distinguish by checking if cost.days_elapsed exists (only new backend has it)
  const hasRealTracking = cost?.days_elapsed != null && trackedMtd >= 0;
  const projectedMo = hasRealTracking ? Math.round((trackedMtd / mtdRatio) * 100) / 100 : 1;

  return {
    key: 'anthropic',
    name: 'Anthropic',
    tier: 'Pay-as-you-go',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 4,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Anthropic"
        health="up"
        tier="Pay-as-you-go"
        quotaBar={
          <CostMetric
            mtd={hasRealTracking ? trackedMtd : null}
            estimate={projectedMo}
            isTracked={hasRealTracking}
            detail={
              hasRealTracking
                ? trackedMtd === 0
                  ? 'No API calls tracked yet this month'
                  : `claude-sonnet-4-6 · day ${daysElapsed}/${daysInMonth}`
                : 'Billing via Anthropic Console'
            }
          />
        }
        hasDetails={false}
      />
    ),
  };
}

// ---- Apple Developer (Annual $99/yr) ----------------------------------------

function buildAppleDeveloperCard(): CardDef {
  return {
    key: 'apple-developer',
    name: 'Apple Developer',
    tier: 'Annual',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 6,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Apple Developer"
        health="up"
        tier="Annual"
        costLine="~$8.25/mo ($99/yr)"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        details={<div style={LABEL_TERT}>artus@appreel.cz &middot; Team QYM9X63C5G</div>}
      />
    ),
  };
}

// ---- Apple App Store (Free) -------------------------------------------------

function buildAppleAppStoreCard(): CardDef {
  return {
    key: 'apple-appstore',
    name: 'Apple App Store',
    tier: 'Free',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 7,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Apple App Store"
        health="up"
        tier="Free"
        costLine="Included with Apple Developer"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        hasDetails={false}
      />
    ),
  };
}

// ---- zoobook.online domain (Annual ~$12/yr) ---------------------------------

function buildDomainCard(): CardDef {
  return {
    key: 'domain',
    name: 'zoobook.online',
    tier: 'Annual',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 6,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="zoobook.online"
        health="up"
        tier="Annual"
        costLine="~$1.00/mo (~$12/yr)"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        details={
          <div style={LABEL_TERT}>DNS via Cloudflare &middot; app / api / platform subdomains</div>
        }
      />
    ),
  };
}

// ---- Cloudflare DNS (Free) --------------------------------------------------

function buildCloudflareDnsCard(): CardDef {
  return {
    key: 'cloudflare-dns',
    name: 'Cloudflare DNS',
    tier: 'Free',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 7,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Cloudflare DNS"
        health="up"
        tier="Free"
        costLine="Free"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        hasDetails={false}
      />
    ),
  };
}

// ---- Docker Hub (Free) ------------------------------------------------------

function buildDockerHubCard(): CardDef {
  return {
    key: 'docker-hub',
    name: 'Docker Hub',
    tier: 'Free',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 7,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Docker Hub"
        health="up"
        tier="Free"
        costLine="200 pulls/6h free · PostGraphile image"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        hasDetails={false}
      />
    ),
  };
}

// ---- Web Push VAPID (Free) --------------------------------------------------

function buildWebPushCard(): CardDef {
  return {
    key: 'web-push',
    name: 'Web Push (VAPID)',
    tier: 'Free',
    health: 'up',
    quotaStatus: 'ok',
    sortPriority: 7,
    hasWarning: false,
    isNotConfigured: false,
    render: () => (
      <ServiceCard
        title="Web Push (VAPID)"
        health="up"
        tier="Free"
        costLine="Free -- self-hosted"
        keyStatus={
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              fontWeight: 600,
              color: 'var(--color-tint-green)',
            }}
          >
            Active
          </div>
        }
        hasDetails={false}
      />
    ),
  };
}

// ---- Not configured cards ---------------------------------------------------

function buildNotConfiguredCard(
  key: string,
  name: string,
  costNote: string,
  description: string,
): CardDef {
  return {
    key,
    name,
    tier: 'Not configured',
    health: 'not_configured',
    quotaStatus: 'ok',
    sortPriority: 8,
    hasWarning: false,
    isNotConfigured: true,
    render: () => (
      <ServiceCard
        title={name}
        health="not_configured"
        tier="Not configured"
        isNotConfigured
        costLine={costNote}
        keyStatus={<div style={LABEL_TERT}>{description}</div>}
        hasDetails={false}
      />
    ),
  };
}

// ============================================================================
// COST OVERVIEW
// ============================================================================

function CostOverview({ cost }: { cost: CostSummary }) {
  const breakdown = cost.breakdown;
  const total = safeNum(cost.total_est_mo);
  const now = new Date();
  const dayOfMonth = cost.days_elapsed ?? now.getUTCDate();
  const daysInMonth =
    cost.days_in_month ??
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
  // MTD: use backend value if present, otherwise pro-rate from total
  const mtd =
    cost.mtd_est != null
      ? safeNum(cost.mtd_est)
      : Math.round(((safeNum(cost.total_est_mo) * dayOfMonth) / daysInMonth) * 100) / 100;
  const projected = safeNum(cost.projected_this_month ?? cost.total_est_mo);

  const supabaseVariable = safeNum(breakdown.supabase_variable_est);
  const supabaseNote =
    supabaseVariable > 0
      ? `$25 plan + ~$${supabaseVariable} variable (egress/storage)`
      : 'Pro plan';
  const anthropicNote =
    safeNum(breakdown.anthropic) < 0.01 ? 'tracked, ~$0 so far' : 'tracked actual';

  const rows: { name: string; amount: number; note: string }[] = [
    { name: 'Supabase', amount: safeNum(breakdown.supabase), note: supabaseNote },
    { name: 'Fly.io Prod', amount: safeNum(breakdown.fly_prod), note: 'tracked uptime' },
    { name: 'Fly.io Staging', amount: safeNum(breakdown.fly_staging), note: 'tracked uptime' },
    { name: 'Apple Developer', amount: safeNum(breakdown.apple_developer), note: '$99/yr' },
    { name: 'Domain', amount: safeNum(breakdown.domain), note: '~$12/yr' },
    { name: 'Anthropic', amount: safeNum(breakdown.anthropic), note: anthropicNote },
  ].filter((r) => r.amount > 0);

  return (
    <div style={{ ...card, padding: '16px 20px' }}>
      {/* Header: big number + MTD/projected */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.6px',
              textTransform: 'uppercase' as const,
              color: 'var(--color-label-tertiary)',
              marginBottom: 4,
            }}
          >
            Monthly Estimate
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                color: total > 150 ? 'var(--color-tint-orange)' : 'var(--color-label-primary)',
              }}
            >
              ${total.toFixed(2)}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'var(--color-label-tertiary)',
                  marginLeft: 2,
                }}
              >
                /mo
              </span>
            </span>
          </div>
        </div>
        {/* MTD + projected column */}
        <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
          <div style={{ ...CAPTION, color: 'var(--color-label-tertiary)', marginBottom: 2 }}>
            Day {dayOfMonth}/{daysInMonth}
          </div>
          <div style={{ ...CAPTION, fontWeight: 600, color: 'var(--color-label-secondary)' }}>
            MTD ~${mtd.toFixed(2)}
          </div>
          <div style={{ ...CAPTION, color: 'var(--color-label-tertiary)' }}>
            Projected ~${projected.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Breakdown rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => {
          const barPct = total > 0 ? Math.min((r.amount / total) * 100, 100) : 0;
          return (
            <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 60,
                  height: 4,
                  borderRadius: 2,
                  background: 'var(--color-fill-tertiary)',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: `${barPct}%`,
                    height: '100%',
                    borderRadius: 2,
                    background: 'var(--color-tint-blue)',
                  }}
                />
              </div>
              <span
                style={{
                  flex: 1,
                  ...CAPTION,
                  color: 'var(--color-label-primary)',
                  fontWeight: 500,
                }}
              >
                {r.name}
              </span>
              <span style={{ ...CAPTION, color: 'var(--color-label-tertiary)' }}>{r.note}</span>
              <span
                style={{
                  ...CAPTION,
                  fontWeight: 700,
                  color: 'var(--color-label-primary)',
                  minWidth: 48,
                  textAlign: 'right' as const,
                }}
              >
                ${r.amount.toFixed(2)}/mo
              </span>
            </div>
          );
        })}
        {/* Free tier line */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 60,
              height: 4,
              borderRadius: 2,
              background: 'var(--color-fill-tertiary)',
              flexShrink: 0,
            }}
          />
          <span style={{ flex: 1, ...CAPTION, color: 'var(--color-label-tertiary)' }}>
            7 free services
          </span>
          <span style={{ ...CAPTION, color: 'var(--color-label-tertiary)' }}>
            Sentry, GitHub, Cloudflare…
          </span>
          <span
            style={{
              ...CAPTION,
              fontWeight: 700,
              color: 'var(--color-tint-green)',
              minWidth: 48,
              textAlign: 'right' as const,
            }}
          >
            $0.00/mo
          </span>
        </div>
      </div>

      {/* Pending */}
      {cost.pending.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--color-border-transparent)',
            ...CAPTION,
            color: 'var(--color-label-tertiary)',
          }}
        >
          Pending: {cost.pending.map((p) => `${p.service} (${p.cost})`).join(' · ')}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STATUS BAR
// ============================================================================

function StatusBar({ cards, timestamp }: { cards: CardDef[]; timestamp: string | null }) {
  const issueCount = cards.filter(
    (c) =>
      !c.isNotConfigured &&
      (c.health === 'down' ||
        c.health === 'degraded' ||
        c.quotaStatus === 'critical' ||
        c.quotaStatus === 'exceeded' ||
        c.hasWarning),
  ).length;

  const allOk = issueCount === 0;
  const dotColor = allOk ? 'var(--color-tint-green)' : 'var(--color-tint-orange)';
  const label = allOk
    ? 'All Systems Operational'
    : `${issueCount} service${issueCount > 1 ? 's' : ''} need${issueCount === 1 ? 's' : ''} attention`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dotColor,
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 'var(--font-size-body)',
          fontWeight: 600,
          color: allOk ? 'var(--color-tint-green)' : 'var(--color-tint-orange)',
          flex: 1,
        }}
      >
        {label}
      </span>
      {timestamp && <span style={LABEL_TERT}>Updated {timestamp}</span>}
    </div>
  );
}

// ============================================================================
// SORTING
// ============================================================================

function getEffectivePriority(c: CardDef): number {
  if (c.isNotConfigured) return 8;
  if (c.quotaStatus === 'critical' || c.quotaStatus === 'exceeded') return 1;
  if (c.health === 'down' || c.hasWarning) return 2;
  if (c.quotaStatus === 'approaching' || c.health === 'degraded') return 3;
  return c.sortPriority;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function MonitoringSection() {
  const data = usePolling<MonitoringData>(
    `${DEV_SERVER}/api/infra/monitoring`,
    15000,
    {} as MonitoringData,
  );

  const hasData = data.success === true;
  const timestamp = data.timestamp ? new Date(data.timestamp).toLocaleTimeString() : null;
  const tiers = data.serviceTiers ?? {};

  if (!hasData) {
    return (
      <div style={{ overflow: 'hidden', minWidth: 0, width: '100%' }}>
        <div style={{ ...card, textAlign: 'center', padding: 40 }}>
          <div
            style={{
              fontSize: 'var(--font-size-body)',
              color: 'var(--color-label-tertiary)',
              marginBottom: 4,
            }}
          >
            Start Dev Tools Server (port 4000) to see infrastructure monitoring.
          </div>
          <div style={LABEL_TERT}>Endpoint: {DEV_SERVER}/api/infra/monitoring</div>
        </div>
      </div>
    );
  }

  // Build all card definitions
  const cards: CardDef[] = [
    // Active services with real data
    buildFlyCard(
      'Fly.io Production',
      data.fly?.prod,
      tiers.fly_prod,
      data.health?.postgraphile,
      data.cost?.breakdown?.fly_prod,
      data.fly?.billing,
    ),
    buildFlyCard(
      'Fly.io Staging',
      data.fly?.staging,
      tiers.fly_staging,
      data.health?.postgraphile_staging,
      data.cost?.breakdown?.fly_staging,
      data.fly?.billing,
    ),
    buildSentryCard(data.sentry, tiers.sentry),
    buildGitHubCard(data.github, tiers.github),
    buildCloudflareCard(data.cloudflare, tiers.cloudflare),
    buildSupabaseCard('Supabase Platform', data.supabase?.platform, tiers.supabase_platform, true),
    buildSupabaseCard('Supabase Staging', data.supabase?.staging, tiers.supabase_staging, false),
    buildSupabaseCard('Supabase Zoo', data.supabase?.zoo, tiers.supabase_zoo, false),
    buildTolgeeCard(data.tolgee, tiers.tolgee),
    buildExpoCard(data.expo, tiers.expo),
    buildResendCard(data.resend, tiers.resend),

    // Pay-as-you-go
    buildAnthropicCard(data.cost),

    // Annual / static info
    buildAppleDeveloperCard(),
    buildAppleAppStoreCard(),
    buildDomainCard(),

    // Infrastructure (simple static)
    buildCloudflareDnsCard(),
    buildDockerHubCard(),
    buildWebPushCard(),

    // Not configured
    buildNotConfiguredCard(
      'google-play',
      'Google Play Console',
      '$25 one-time registration',
      'Android publishing not enabled',
    ),
    buildNotConfiguredCard(
      'azure-signing',
      'Azure Code Signing',
      'TBD',
      'Windows MSIX signing not set up',
    ),
    buildNotConfiguredCard(
      'macos-signing',
      'macOS Code Signing',
      'Apple Developer $99/yr',
      'CSC_IDENTITY_AUTO_DISCOVERY=false — unsigned builds only',
    ),
    buildNotConfiguredCard(
      'gitlab-migration',
      'GitLab CI/CD',
      'Free (self-hosted runners $0)',
      'Planned: migrate from GitHub — no OS multiplier, billing API, K8s integration',
    ),
  ];

  // Sort cards by priority
  const sortedCards = [...cards].sort((a, b) => getEffectivePriority(a) - getEffectivePriority(b));

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 16, overflow: 'hidden', minWidth: 0 }}
    >
      {/* A. Status Bar */}
      <StatusBar cards={cards} timestamp={timestamp} />

      {/* B. Cost Overview */}
      {data.cost && <CostOverview cost={data.cost} />}

      {/* C. Service Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
          gap: 14,
          alignItems: 'stretch',
        }}
      >
        {sortedCards.map((c) => (
          <div key={c.key}>{c.render()}</div>
        ))}
      </div>
    </div>
  );
}
